import React from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';

export type ReviewerPrimaryAction = 'acknowledge' | 'start_review' | 'create_work_order' | 'resolve' | 'none';

export const deriveReviewerPrimaryAction = (params: {
  canManage: boolean;
  canCreateWorkOrder: boolean;
  status: string;
  hasOdooTicket: boolean;
  hasFollowUpSignal?: boolean;
}): ReviewerPrimaryAction => {
  if (!params.canManage) return 'none';
  const s = String(params.status || 'OPEN').toUpperCase();
  if (s === 'CLOSED' || s === 'SUPPRESSED') return 'none';
  if (s === 'OPEN') return 'acknowledge';
  if (s === 'ACKED') return 'start_review';
  if (s === 'IN_REVIEW') {
    if (params.canCreateWorkOrder && !params.hasOdooTicket && params.hasFollowUpSignal) {
      return 'create_work_order';
    }
    return 'resolve';
  }
  return 'none';
};

export const deriveReviewerGuidance = (params: { primary: ReviewerPrimaryAction; status: string }): string => {
  const s = String(params.status || '').toUpperCase();
  if (s === 'CLOSED' || s === 'SUPPRESSED') {
    return 'This incident is already closed. Use the timeline to review the outcome, notes, and prior actions.';
  }
  switch (params.primary) {
    case 'acknowledge':
      return 'Acknowledge this incident to establish ownership before deeper review.';
    case 'start_review':
      return 'Start review once ownership is clear so you can validate evidence and decide next action.';
    case 'create_work_order':
      return 'Create tracked follow-up when this incident needs maintenance or operations remediation.';
    case 'resolve':
      return 'Close the incident when the evidence is confirmed and the operator review can end.';
    default: {
      return 'Use the follow-up sections to route ownership, capture notes, and record work that may continue after the review closes.';
    }
  }
};

export const deriveReviewerCanonicalContext = (params: {
  reviewOutcomeLabel?: string | null;
  evidenceReadinessLabel?: string | null;
  hasOperatorNote?: boolean;
}): string => {
  const reviewOutcomeLabel = String(params.reviewOutcomeLabel || '').trim();
  const evidenceReadinessLabel = String(params.evidenceReadinessLabel || '').trim();
  const noteSuffix = params.hasOperatorNote ? ' An operator note is already on the case.' : '';
  if (reviewOutcomeLabel && evidenceReadinessLabel) {
    return `Review status: ${reviewOutcomeLabel}. Evidence readiness: ${evidenceReadinessLabel}.${noteSuffix}`;
  }
  if (reviewOutcomeLabel) {
    return `Review status: ${reviewOutcomeLabel}.${noteSuffix}`;
  }
  if (evidenceReadinessLabel) {
    return `Evidence readiness: ${evidenceReadinessLabel}.${noteSuffix}`;
  }
  if (params.hasOperatorNote) {
    return 'An operator note is already on the case.';
  }
  return '';
};

export interface ReviewerActionBarProps {
  canManageIncidentWorkflow: boolean;
  canOpenDiagnostics: boolean;
  canAddComment: boolean;
  canResolve: boolean;
  canCreateWorkOrder: boolean;
  canSyncExternal: boolean;
  /** Which single action to emphasize as the next review step. */
  primaryAction: ReviewerPrimaryAction;
  /** Short guidance under the title (state-aware). */
  guidance: string;
  canonicalContext?: string | null;
  busyLabel: string | null;
  onAcknowledge: () => void;
  onOpenDiagnostics: () => void;
  onResolve: () => void;
  onDismissOrClose: () => void;
  onAddComment: () => void;
  onStartReview: () => void;
  onCreateWorkOrder: () => void;
  onSyncExternal: () => void;
  /** `INCIDENT` workflow status for hiding impossible controls in “More actions”. */
  incidentWorkflowStatus?: string | null;
  /** Epic 93 grouped recurring-risk — drop legacy step numbers and use panel titles. */
  groupedReviewMode?: boolean;
  headingTitle?: string;
  headingSubtitle?: string;
  footerNote?: string;
  /** Grouped Assign Action — lock acknowledge and related CTAs until action owner is set. */
  assignActionSectionsLocked?: boolean;
  /** Observation disposition — ownership is separate from disposition outcomes. */
  observationDispositionMode?: boolean;
}

const ReviewerActionBar: React.FC<ReviewerActionBarProps> = ({
  canManageIncidentWorkflow,
  canOpenDiagnostics,
  canAddComment,
  canResolve,
  canCreateWorkOrder,
  canSyncExternal,
  primaryAction,
  guidance,
  canonicalContext,
  busyLabel,
  onAcknowledge,
  onOpenDiagnostics,
  onResolve,
  onDismissOrClose,
  onAddComment,
  onStartReview,
  onCreateWorkOrder,
  onSyncExternal,
  incidentWorkflowStatus,
  groupedReviewMode = false,
  headingTitle,
  headingSubtitle,
  footerNote,
  assignActionSectionsLocked = false,
  observationDispositionMode = false,
}) => {
  const groupedSectionsLocked = groupedReviewMode && assignActionSectionsLocked;
  const ownershipOnlyMode = observationDispositionMode;
  // Wide string: workflow can include CLOSED / SUPPRESSED; avoid union narrowing for runtime checks
  const w: string = String(incidentWorkflowStatus ?? 'OPEN').toUpperCase();
  const showAck = canManageIncidentWorkflow && w === 'OPEN';
  const showAckPrimary = showAck && primaryAction === 'acknowledge' && !ownershipOnlyMode;
  const showStartReview =
    canManageIncidentWorkflow && w !== 'CLOSED' && w !== 'SUPPRESSED' && (w === 'OPEN' || w === 'ACKED' || w === 'IN_REVIEW');
  // Must acknowledge before review is a realistic next step from OPEN.
  const startReviewEligible = w === 'ACKED' || w === 'IN_REVIEW';
  const showResolve = canResolve && canManageIncidentWorkflow && (w === 'IN_REVIEW' || w === 'ACKED');
  const showCreateWo = canCreateWorkOrder && w === 'IN_REVIEW';
  const showDismiss = canManageIncidentWorkflow && !showResolve && w !== 'CLOSED' && w !== 'SUPPRESSED';
  const showWorkflowPrimary = !ownershipOnlyMode;

  const title = headingTitle || (groupedReviewMode ? 'Acknowledge representative evidence' : 'Step 5: Choose the next action');
  const supportingNote = groupedSectionsLocked
    ? 'Complete Step 1 before acknowledging this representative evidence.'
    : footerNote
      ?? (groupedReviewMode
        ? 'Acknowledge after follow-up work is planned and the next review is scheduled.'
        : ownershipOnlyMode
          ? ''
          : 'Keep this focused on the review decision. Action ownership, work-order linkage, and impact measurement live in the sections below.');

  return (
    <Paper variant="outlined" sx={{ p: 1.75, mb: 1.5, borderRadius: 3, borderColor: '#e4e7ec' }} data-testid="reviewer-action-bar">
      <Typography sx={{ fontSize: 15, fontWeight: 800, mb: headingSubtitle ? 0.35 : 0.5 }}>{title}</Typography>
      {headingSubtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.45 }}>
          {headingSubtitle}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.45 }}>
        {guidance}
      </Typography>
      {canonicalContext ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.1, lineHeight: 1.45 }}>
          {canonicalContext}
        </Typography>
      ) : null}
      {supportingNote ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.1 }}>
          {supportingNote}
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.25 }}>
        {showAckPrimary && (
          <Button
            size="medium"
            variant="contained"
            disabled={groupedSectionsLocked}
            onClick={onAcknowledge}
            sx={{ fontWeight: 800 }}
          >
            Acknowledge
          </Button>
        )}
        {showStartReview && showWorkflowPrimary && primaryAction === 'start_review' && (
          <Button size="medium" variant="contained" onClick={onStartReview} sx={{ fontWeight: 800 }}>
            Start review
          </Button>
        )}
        {showCreateWo && showWorkflowPrimary && primaryAction === 'create_work_order' && (
          <Button size="medium" variant="contained" onClick={onCreateWorkOrder} sx={{ fontWeight: 800 }}>
            Create work order
          </Button>
        )}
        {showResolve && showWorkflowPrimary && primaryAction === 'resolve' && (
          <Button size="medium" variant="contained" color="success" onClick={onResolve} sx={{ fontWeight: 800 }}>
            Close incident
          </Button>
        )}

        {showResolve && showWorkflowPrimary && primaryAction === 'create_work_order' && (
          <Button size="small" variant="outlined" color="success" onClick={onResolve}>
            Close incident instead
          </Button>
        )}
        {showCreateWo && showWorkflowPrimary && primaryAction === 'resolve' && (
          <Button size="small" variant="outlined" onClick={onCreateWorkOrder}>
            Create work order
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 700 }}>
        More actions
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {showAck && (ownershipOnlyMode || primaryAction !== 'acknowledge') && (
          <Button size="small" variant="outlined" disabled={groupedSectionsLocked} onClick={onAcknowledge}>
            Acknowledge
          </Button>
        )}
        {showStartReview && startReviewEligible && (ownershipOnlyMode || primaryAction !== 'start_review') && (
          <Button size="small" variant="outlined" onClick={onStartReview}>
            Start review
          </Button>
        )}
        {canAddComment && (
          <Button size="small" variant="text" onClick={onAddComment}>
            Add comment
          </Button>
        )}
        {canOpenDiagnostics && (
          <Button size="small" variant="text" color="inherit" onClick={onOpenDiagnostics}>
            Open diagnostics
          </Button>
        )}
        {canSyncExternal && (
          <Button size="small" variant="text" color="inherit" onClick={onSyncExternal}>
            Sync to Odoo
          </Button>
        )}
        {showCreateWo && (ownershipOnlyMode || primaryAction !== 'create_work_order') && (
          <Button size="small" variant="text" color="inherit" onClick={onCreateWorkOrder}>
            Create work order
          </Button>
        )}
        {showResolve && (ownershipOnlyMode || primaryAction !== 'resolve') && (
          <Button size="small" variant="text" color="success" onClick={onResolve}>
            Close incident
          </Button>
        )}
        {showDismiss && (
          <Button size="small" variant="text" color="inherit" onClick={onDismissOrClose}>
            Close options
          </Button>
        )}
      </Box>

      {busyLabel && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {busyLabel}…
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ReviewerActionBar;
